import {
  getMeetingPlanByCategory,
  isZoneBundleContext,
  ZOOM_MEETING_SUB_TYPES,
} from '@/plans/ZOOM_MEETING/common/utils';
import { plansof } from '@/utils/chain';
import {
  BASE_PLAN_SUB_TYPES,
  BILLING_CYCLE,
  BIZ_PLUS_PLAN_CODES_ONLINE_SUPPORTED,
  PBX_CATEGORYS,
  planTypes,
  PRO_PLUS_PLAN_CODES_SUPPORTED,
  ZOOM_MEETING_PRO_MIN_LICENSE,
  trackingPlanTypes,
} from '@/common/constants';
import { getPlanTypeByCategory } from '@/common/service/parse';
import { $query } from '@/global-install';
import { phoneNumberStorage } from '@/plans/ZOOM_PHONE/store';
import { zoneBundleUtils } from '@/plans/ZOOM_ONE_BUNDLE/utils';
import { ONLINE_NOT_SUPPORT_BILLING_CYCLE } from '@/common/service/non-standard-billing-cycle-utils';
import { computedPbxBasePlans, computedPbxNonBasePlans } from '@/common/service/computers/plans/pbx';
import { getMigrateFlowType, MIGRATE_FLOW_TYPES, migrationPlanTypes } from '@/plans/ZOOM_MEETING/dialog/migration-plan';
import { get, groupBy } from 'lodash';
import { mapGetters } from 'vuex';
import { getPbxCurrentBillingCycle } from '@/plans/ZOOM_PHONE/helper/pbx-billing-cycle';

const hostCache = {};

export default {
  computed: {
    ...mapGetters(['boughtPlans', 'currentEditPlan', 'getSelectedPlans']),
    pbxBasePlan() {
      return computedPbxBasePlans(this.boughtPlans)?.[0];
    },
    selectedPlansFromTypeGroup() {
      return (this.getTypeGroupSelectedPlans?.[planTypes.ZOOM_MEETING]
        || this.getTypeGroupSelectedPlans?.[planTypes.ZOOM_ONE_BUNDLE]
        || this.getTypeGroupSelectedPlans?.[planTypes.ZONE_PRO_PLUS])?.[0] ?? {};
    }
  },
  methods: {
    // col -> next click button
    async handleSwitchMeetingPlan(col) {
      if (col?.btn?.disabled) return;

      // current edit plan
      if (this.$context.is.edit) {
        const currentEditPlan = this.selectedPlansFromTypeGroup;
        const originalEditPlan = this.currentEditPlan;
        const currentEditPlanCategory = currentEditPlan.zoomMeetingCategory;
        const originEditPlanCategory = originalEditPlan.zoomMeetingCategory;
        const nextEditPlanCategory = col.category;
        const migrateType = migrationPlanTypes({
          originEditPlanCategory, currentEditPlanCategory, nextEditPlanCategory
        });
        const action = this.getFnByMigrateTypes();
        console.log(`[migration-plan] =>`, migrateType);
        if (migrateType && action[migrateType]) {
          await action[migrateType].call(this, { col, currentEditPlanCategory, migrateType, originEditPlanCategory, nextEditPlanCategory });
          this.setMigratePlanState({
            migrateType,
            flow: getMigrateFlowType(migrateType)
          });
        }
        console.log(`[migration-plan cache] =>`, {...this.migratePlanState.hotCache});
        // Google Analytics
        if (this.migratePlanState.is.upgrade || this.migratePlanState.is.downgrade) {
          const {trackingName, productName} = this.getGATrackingName({originEditPlanCategory, nextEditPlanCategory});
          this.$tracking.buyInteraction(`product details`, `${trackingName} full plan comparison select - ${productName}`);
        }
        this.handleSure();
        return Promise.reject();
      }

      if (this.$context.is.add2cart) {
        if (isZoneBundleContext
          || [BASE_PLAN_SUB_TYPES.ZOOM_ONE_BIZ_PLUS, BASE_PLAN_SUB_TYPES.ZOOM_ONE_PRO_PLUS
          ].includes(col.category)) {
          location.replace(col.btn.link);
          return;
        }
      }

      // !!! just for buyNow
      await this.switchProBizUrl(col);

      this.$tracking.buyInteraction('product details', `full plan comparison select - ${col?.category}`);

      switch(col.category) {
        case ZOOM_MEETING_SUB_TYPES.PRO:
        case ZOOM_MEETING_SUB_TYPES.BIZ:
          return this.switchMeetingPlanCategory(col);
        case ZOOM_MEETING_SUB_TYPES.ZOOM_ONE_BIZ_PLUS:
        case ZOOM_MEETING_SUB_TYPES.ZOOM_ONE_PRO_PLUS:
        default:
          return this.jumpToOldFlow(col);
      }
    },
    switchProBizUrl(col){
      if(isZoneBundleContext) {
        const billingCycle = plansof(this.getSelectedPlans).type(this.getCurrentBasePlanInfo.planType).get(0)?.billing_cycle || BILLING_CYCLE.ANNUAL;
        const currentPlanType = getPlanTypeByCategory($query.from);
        const curZoneBundleType = [planTypes.ZOOM_ONE_BUNDLE, planTypes.ZONE_PRO_PLUS].find((p) => p === currentPlanType);
        const nextPlanCategory = col.category;
        let plan = col.category;

        let from = curZoneBundleType ? "" : $query.from;

        if (curZoneBundleType) {
          const hasZoneProPlus = plansof(this.getSelectedPlans).type(planTypes.ZONE_PRO_PLUS).has();

          // when the customer current visit zoom one pro plus, then switch should remove phone number store
          hasZoneProPlus && phoneNumberStorage.remove();

          if (nextPlanCategory === ZOOM_MEETING_SUB_TYPES.ZOOM_ONE_PRO_PLUS) {
            plan = ZOOM_MEETING_SUB_TYPES.PRO_PLUS;
            from = zoneBundleUtils.PRO_PLUS_CATEGORY;
          }

          if (nextPlanCategory === ZOOM_MEETING_SUB_TYPES.ZOOM_ONE_BIZ_PLUS) {
            from = zoneBundleUtils.BIZ_PLUS_CATEGORY;
          }
        }

        location.href = `/opc/buy/config?plan=${plan}&period=${billingCycle}&from=${from}&type=${$query.type??''}`;
        return Promise.reject();
      }
      return Promise.resolve();
    },
    async loadCache({currentEditPlanCategory, nextEditPlanCategory}) {
      if (!this.migratePlanState.flow) return;
      const oldHotCache = this.migratePlanState.hotCache?.[nextEditPlanCategory];
      const newHotCache = {...this.migratePlanState.hotCache};
      newHotCache[currentEditPlanCategory] = this.getSelectedPlans.map(p => ({ planCode: p.plan_code, type: p.type, cartInfo: { ...p.cartInfo } }));
      this.getSelectedPlans.forEach(p => {
        this.removePlan(p?.plan_code);
      });
      if (oldHotCache?.length) {
        oldHotCache.forEach(p => {
          this.updateSelectPlan({
            plan_code: p.planCode,
            cartInfo: p.cartInfo
          });
        });
      }
      // refresh plan card
      this.setMigratePlanState({
        planList: [],
        flow: '',
        hotCache: newHotCache
      });
    },
    async bizPlusDowngradeToBiz({currentEditPlanCategory, nextEditPlanCategory}) {
      // downgrade message
      const { selectedBizPlusPlan, selectedPbxBasePlan, totalLicenseWithoutFreeTrial, maxLicensesAddonPlan, assignedBizPlusLicenseQuantity } = this.plansofBizBundle;
      const maxAddonLicense = maxLicensesAddonPlan?.cartInfo?.selected_number ?? 0;
      const quantity = Math.max(maxAddonLicense, assignedBizPlusLicenseQuantity, totalLicenseWithoutFreeTrial, ZOOM_MEETING_PRO_MIN_LICENSE);

      const planList = selectedBizPlusPlan.map(p => {
        const extraKeys = {}
        if (ONLINE_NOT_SUPPORT_BILLING_CYCLE.includes(p.billing_cycle)) {
          Object.assign(extraKeys, { oldPlanType: p.plan_type })
        }
        return {
          oldPlanCode: p.plan_code,
          quantity: p.cartInfo.selected_number,
          ...extraKeys
        }
      });
      const { hotCache, oldHotCache } = this.getHotCache({
        currentEditPlanCategory,
        nextEditPlanCategory,
        plans: [...selectedBizPlusPlan, selectedPbxBasePlan].map(p => ({ plan_code: p.plan_code, type: p.type, cartInfo: { ...p.cartInfo } }))
      });
      // remove biz+ plans
      selectedBizPlusPlan.forEach(p => {
        this.removePlan(p.plan_code);
      });
      // remove pbx base plan
      this.removePlan(selectedPbxBasePlan.plan_code);
      // add biz plan to cart, keep same cycle with biz plus
      const billingCycle = selectedBizPlusPlan[0].billing_cycle;
      const bizPlan = this.planMapFlat.find(p => p.billing_cycle === billingCycle && p.type === planTypes.ZOOM_MEETING && p.category === BASE_PLAN_SUB_TYPES.BIZ);
      if (oldHotCache?.length) {
        oldHotCache.forEach(p => {
          this.updateSelectPlan({
            plan_code: bizPlan.plan_code,
            cartInfo: p.cartInfo
          });
        });
      } else {
        this.updateSelectPlan({
          plan_code: bizPlan.plan_code,
          cartInfo: { selected_number: quantity }
        });
      }
      // refresh plan card
      this.setMigratePlanState({
        planList,
        hotCache
      });
    },
    async upgradeToBizPlus({nextEditPlanCategory,currentEditPlanCategory}) {
      const boughtMeetingPlanLicense = plansof(this.boughtPlans).type(planTypes.ZOOM_MEETING).get(0)?.cartInfo?.selected_number ?? 0;
      const { selectedBizPlan, boughtBizPlan } = this.plansofBizBundle;
      const bizPlan = selectedBizPlan.length ? selectedBizPlan : boughtBizPlan;
      const quantity = Math.max(bizPlan?.[0]?.cartInfo?.selected_number, boughtMeetingPlanLicense, 10);
      const planList = bizPlan.map(p => {
        return {
          oldPlanCode: p.plan_code,
          quantity: p.cartInfo.selected_number,
        }
      });
      const { oldHotCache,hotCache} = this.getHotCache({nextEditPlanCategory,currentEditPlanCategory, plans: [...bizPlan]})
      // remove biz plan
      bizPlan.forEach(p => {
        this.removePlan(p.plan_code);
      });
      // load cache
      if (oldHotCache?.length) {
        oldHotCache.forEach(p => {
          this.updateSelectPlan({
            plan_code: p.planCode,
            cartInfo: { selected_number: p.type === planTypes.ZOOM_ONE_BUNDLE ? quantity : p.cartInfo.selected_number }
          });
        });
      } else {
        const boughtPbxBasePlans = computedPbxBasePlans(this.boughtPlans);
        const boughtPbxPlans = computedPbxNonBasePlans(this.boughtPlans);
        const boughtPbxBasePlan = boughtPbxBasePlans?.[0];
        const boughtMeetingPlan = plansof(this.boughtPlans).type(planTypes.ZOOM_MEETING).get(0);
        let billingCycle =  boughtMeetingPlan.billing_cycle || BILLING_CYCLE.ANNUAL;

        if (boughtPbxBasePlan) {
          billingCycle = getPbxCurrentBillingCycle(boughtPbxBasePlan, boughtPbxPlans?.[0]);
        }

        // add biz plus plans to cart
        const bizPlusPlan = this.planMapFlat.find((x) => x.billing_cycle === billingCycle && x.type === planTypes.ZOOM_ONE_BUNDLE && BIZ_PLUS_PLAN_CODES_ONLINE_SUPPORTED.includes(x.plan_code));
        let pbxBasePlan = boughtPbxBasePlans?.[0] || computedPbxBasePlans(this.planMapFlat).find(p => p.category === PBX_CATEGORYS.ZVP);
        // biz plus
        this.updateSelectPlan({
          plan_code: bizPlusPlan.plan_code,
          cartInfo: { selected_number: quantity }
        });
        // pbx base plan
        this.updateSelectPlan({
          plan_code: pbxBasePlan.plan_code,
          cartInfo: { selected_number: 1 }
        });
      }
      // refresh plan card
      this.setMigratePlanState({
        planList,
        hotCache
      });
    },
    async switchMeetingPlanCategory({ category }) {
      const currentBasePlan = get(this.getCurrentBasePlanInfo, 'plans.0', null);
      const targetPlans = this.planMapFlat.filter(item => item.type === planTypes.ZOOM_MEETING & item.category === category);
      const { max, min } = get(targetPlans, '0', { max: 1, min: 99 });
      let billingCycle = BILLING_CYCLE.ANNUAL;
      let defaultLicenses = min;
      let oldPlanCode = get(currentBasePlan, 'plan_code', '');
      let oldPlanCategory = get(currentBasePlan, 'category', '');
      let oldPlanHost = get(currentBasePlan, 'cartInfo.selected_number', '');
      category && (hostCache[oldPlanCategory] = oldPlanHost);
      if(oldPlanCode) {
        const { billing_cycle, cartInfo } = currentBasePlan;
        billingCycle = billing_cycle;
        if(cartInfo.selected_number < min) {
          // If pro switch to biz default license is min of biz.
          defaultLicenses = min;
        } else if(this.$context.is.edit) {
          defaultLicenses = cartInfo.selected_number;
        } else if(cartInfo.selected_number > max) {
          // If biz switch to pro default license is max of pro.
          defaultLicenses = max;
        }
      }
      const exceedBaseLicensePlans = this.getSelectedPlans.filter(item => (
        item.cartInfo.selected_number > defaultLicenses &&
        item.type !== planTypes.ZOOM_MEETING &&
        this.limitLicenseWithBaseZoneTypes.includes(item.type)
      ));
      const nextMeetingPlan = targetPlans.find(item => item.billing_cycle === billingCycle) || targetPlans[0];
      if(hostCache[category]) {
        defaultLicenses = hostCache[nextMeetingPlan.category];
      }
      if(exceedBaseLicensePlans.length > 0) {
        const planGroupByType = groupBy(exceedBaseLicensePlans, 'type')
        const typeNames = Object.keys(planGroupByType).map(type => {
          const typeNameKey = get(planGroupByType[type], '0.typeNameKey', '');
          return this.$t(typeNameKey);
        });

        // MIX_BUY
        if (!this.$context.is.mixBuy) {
          await this.$confirm(
            this.$t('billing.zone.host_need_adjust.content', [typeNames.join(',')]),
            this.$t('billing.zone.host_need_adjust.title', [typeNames.join(',')]),
            {
              confirmButtonText: this.$t('billing.common.confirm'),
              cancelButtonText: this.$t('billing.common.cancel'),
            }
          );
        }
        exceedBaseLicensePlans.forEach(item => {
          this.updateSelectPlan({
            plan_code: item.plan_code,
            cartInfo: {
              ...item.cartInfo,
              selected_number: defaultLicenses,
            },
          });
        })
      }

      const cartInfo = { selected_number: defaultLicenses };

      await this.switchCategoryAction([oldPlanCode, {...nextMeetingPlan, cartInfo }]);

      this.handleSure();
    },
    jumpToOldFlow({ btn }) {
      location.replace(btn.link);
    },
    async proUpgrade2proPlus({currentEditPlanCategory, nextEditPlanCategory}) {
      const selectedMeetingPlan = this.getSelectedPlans.find(p => p.type === planTypes.ZOOM_MEETING);
      const boughtProPlan = getMeetingPlanByCategory(this.boughtPlans, BASE_PLAN_SUB_TYPES.PRO);
      const quantity = Math.max(boughtProPlan.cartInfo.selected_number, selectedMeetingPlan.cartInfo.selected_number);
      const planList = this.getSelectedPlans.map(p => {
        return {
          oldPlanCode: p.plan_code,
          quantity: p.cartInfo.selected_number,
        }
      });
      const { oldHotCache, hotCache} = this.getHotCache({currentEditPlanCategory, nextEditPlanCategory});
      // remove pro plus plan
      this.removePlan(selectedMeetingPlan.plan_code);
      // load cache
      if (oldHotCache?.length) {
        oldHotCache.forEach(p => {
          this.updateSelectPlan({
            plan_code: p.planCode,
            cartInfo: { selected_number: p.cartInfo.selected_number }
          });
        });
      } else {
        let billingCycle =  selectedMeetingPlan.billing_cycle || BILLING_CYCLE.ANNUAL;

        const boughtPbxBasePlans = computedPbxBasePlans(this.boughtPlans);
        const boughtPbxPlans = computedPbxNonBasePlans(this.boughtPlans);
        const boughtPbxBasePlan = boughtPbxBasePlans?.[0];

        if (boughtPbxBasePlan) {
          billingCycle = getPbxCurrentBillingCycle(boughtPbxBasePlan, boughtPbxPlans?.[0]);
        }

        // add pro plus plans to cart
        const proplus = this.planMapFlat.find((x) => x.billing_cycle === billingCycle && x.type === planTypes.ZONE_PRO_PLUS && PRO_PLUS_PLAN_CODES_SUPPORTED.includes(x.plan_code));

        let pbxBasePlan = boughtPbxBasePlans?.[0] || computedPbxBasePlans(this.planMapFlat).find(p => p.category === PBX_CATEGORYS.ZVP);
        // pro plus
        this.updateSelectPlan({
          plan_code: proplus.plan_code,
          cartInfo: { selected_number: quantity }
        });
        // pbx base plan
        this.updateSelectPlan({
          plan_code: pbxBasePlan.plan_code,
          cartInfo: { selected_number: 1 }
        });
      }
      // refresh plan card
      this.setMigratePlanState({
        planList,
        hotCache
      });
    },
    async proPlusUpgrade2bizPlus({currentEditPlanCategory, nextEditPlanCategory}) {
      const proPlus = this.getSelectedPlans.find(p => p.type === planTypes.ZONE_PRO_PLUS);
      const boughtProp = this.boughtPlans.find(p => p.type === planTypes.ZONE_PRO_PLUS);
      const quantity = Math.max(boughtProp?.cartInfo?.selected_number, proPlus?.cartInfo?.selected_number, ZOOM_MEETING_PRO_MIN_LICENSE);
      const planList = this.getSelectedPlans.map(p => {
        return {
          oldPlanCode: p.plan_code,
          quantity: p.cartInfo.selected_number,
        }
      });
      const { oldHotCache, hotCache} = this.getHotCache({currentEditPlanCategory, nextEditPlanCategory});
      // remove pro plus plan
      this.removePlan(this.currentEditPlan.plan_code);
      // load cache
      if (oldHotCache?.length) {
        oldHotCache.forEach(p => {
          this.updateSelectPlan({
            plan_code: p.planCode,
            cartInfo: { selected_number: p.cartInfo.selected_number }
          });
        });
      } else {
        let billingCycle =  this.currentEditPlan.billing_cycle || BILLING_CYCLE.ANNUAL;
        // add biz plus plans to cart
        const bizPlusPlan = this.planMapFlat.find((x) => x.billing_cycle === billingCycle && x.type === planTypes.ZOOM_ONE_BUNDLE && BIZ_PLUS_PLAN_CODES_ONLINE_SUPPORTED.includes(x.plan_code));
        // biz plus
        this.updateSelectPlan({
          plan_code: bizPlusPlan.plan_code,
          cartInfo: { selected_number: quantity }
        });
      }
      // refresh plan card
      this.setMigratePlanState({
        planList,
        hotCache
      });
    },
    getHotCache({nextEditPlanCategory,currentEditPlanCategory, plans = this.getSelectedPlans}) {
      const oldHotCache = this.migratePlanState.hotCache?.[nextEditPlanCategory];
      const hotCache = { ...this.migratePlanState.hotCache };
      hotCache[currentEditPlanCategory] = [...plans].map(p => ({ planCode: p.plan_code, type: p.type, cartInfo: { ...p.cartInfo } }));
      return {
        hotCache,
        oldHotCache
      }
    },
    setHotCache({currentEditPlanCategory, nextEditPlanCategory}) {
      // set cache
      const { hotCache } = this.getHotCache({
        currentEditPlanCategory,
        nextEditPlanCategory,
      });

      // refresh plan card
      this.setMigratePlanState({
        hotCache
      });
    },
    getFnByMigrateTypes() {
      return {
        [MIGRATE_FLOW_TYPES.pro2bundle]({ col }) {
          return this.jumpToOldFlow(col);
        },
        [MIGRATE_FLOW_TYPES.pro2biz]({ col, currentEditPlanCategory, originEditPlanCategory, nextEditPlanCategory }) {
          this.setHotCache({ currentEditPlanCategory, nextEditPlanCategory });
          if (currentEditPlanCategory === ZOOM_MEETING_SUB_TYPES.ZOOM_ONE_PRO_PLUS) {
            this.getSelectedPlans.forEach(p => {
              this.removePlan(p.plan_code);
            });
            // load pro
            const oldHotCache = this.migratePlanState.hotCache?.[originEditPlanCategory];
            if (oldHotCache?.length) {
              oldHotCache.forEach(p => {
                this.updateSelectPlan({
                  plan_code: p.planCode,
                  cartInfo: p.cartInfo
                });
              });
            }
          }
          return this.switchMeetingPlanCategory(col);
        },
        [MIGRATE_FLOW_TYPES.bizBack2pro]({ col, currentEditPlanCategory, nextEditPlanCategory }) {
          this.setHotCache({currentEditPlanCategory, nextEditPlanCategory});
          return this.switchMeetingPlanCategory(col);
        },
        [MIGRATE_FLOW_TYPES.bundleBack2pro]() {},
        [MIGRATE_FLOW_TYPES.biz2bundle]({ currentEditPlanCategory, nextEditPlanCategory }) {
          // remove pro plan
          if (currentEditPlanCategory === ZOOM_MEETING_SUB_TYPES.PRO) {
            this.getSelectedPlans.forEach(p => {
              this.removePlan(p.plan_code);
            });
          }
          return this.upgradeToBizPlus({ nextEditPlanCategory,currentEditPlanCategory });
        },
        [MIGRATE_FLOW_TYPES.biz2pro]({ col, currentEditPlanCategory, nextEditPlanCategory }) {
          this.setHotCache({currentEditPlanCategory, nextEditPlanCategory});
          this.loadCache({currentEditPlanCategory, nextEditPlanCategory});
          return this.switchMeetingPlanCategory(col);
        },
        [MIGRATE_FLOW_TYPES.bundleBack2biz]({ currentEditPlanCategory, nextEditPlanCategory }) {
          return this.loadCache({ currentEditPlanCategory, nextEditPlanCategory });
        },
        [MIGRATE_FLOW_TYPES.proBack2biz]({ col, currentEditPlanCategory, nextEditPlanCategory }) {
          this.setHotCache({currentEditPlanCategory, nextEditPlanCategory});
          return this.switchMeetingPlanCategory(col,);
        },
        [MIGRATE_FLOW_TYPES.bundle2biz]({ currentEditPlanCategory, nextEditPlanCategory }) {
          return this.bizPlusDowngradeToBiz({ currentEditPlanCategory, nextEditPlanCategory });
        },
        [MIGRATE_FLOW_TYPES.bizBack2bundle]({ currentEditPlanCategory, nextEditPlanCategory }) {
          return this.loadCache({ currentEditPlanCategory, nextEditPlanCategory });
        },
        [MIGRATE_FLOW_TYPES.bundle2pro]() {},
        [MIGRATE_FLOW_TYPES.pro2prop]({currentEditPlanCategory, nextEditPlanCategory}) {
          return this.proUpgrade2proPlus({currentEditPlanCategory, nextEditPlanCategory});
        },
        [MIGRATE_FLOW_TYPES.prop2bundle]({currentEditPlanCategory, nextEditPlanCategory}) {
          return this.proPlusUpgrade2bizPlus({currentEditPlanCategory, nextEditPlanCategory});
        },
        [MIGRATE_FLOW_TYPES.bundleBack2proplus]({currentEditPlanCategory, nextEditPlanCategory}) {
          return this.loadCache({currentEditPlanCategory, nextEditPlanCategory});
        },
        [MIGRATE_FLOW_TYPES.proplusBack2pro]({nextEditPlanCategory, currentEditPlanCategory}) {
          return this.loadCache({nextEditPlanCategory, currentEditPlanCategory});
        },
      };
    },
    getGATrackingName({originEditPlanCategory, nextEditPlanCategory}) {
      let trackingName = '', productName = '';
      switch (originEditPlanCategory) {
        case ZOOM_MEETING_SUB_TYPES.PRO:
          trackingName = trackingPlanTypes.ZOOM_MEETING + ' pro';
          break;
        case ZOOM_MEETING_SUB_TYPES.BIZ:
          trackingName = trackingPlanTypes.ZOOM_MEETING + ' biz';
          break;
        case ZOOM_MEETING_SUB_TYPES.ZOOM_ONE_PRO_PLUS:
          trackingName = trackingPlanTypes.ZONE_PRO_PLUS;
          break;
        case ZOOM_MEETING_SUB_TYPES.ZOOM_ONE_BIZ_PLUS:
          trackingName = trackingPlanTypes.ZONE_BIZ_PLUS;
          break;
      }
      switch (nextEditPlanCategory) {
        case ZOOM_MEETING_SUB_TYPES.PRO:
          productName = trackingPlanTypes.ZOOM_MEETING + ' pro';
          break;
        case ZOOM_MEETING_SUB_TYPES.BIZ:
          productName = trackingPlanTypes.ZOOM_MEETING + ' biz';
          break;
        case ZOOM_MEETING_SUB_TYPES.ZOOM_ONE_PRO_PLUS:
          productName = trackingPlanTypes.ZONE_PRO_PLUS;
          break;
        case ZOOM_MEETING_SUB_TYPES.ZOOM_ONE_BIZ_PLUS:
          productName = trackingPlanTypes.ZONE_BIZ_PLUS;
          break;
      }
      return { trackingName, productName };
    },
  }
}
